import { Typography, type TypographyProps } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode, Ref } from "react";
import { typography } from "../../theme/tokens/typography.js";

/**
 * PageHeading — the canonical `<h1>` style for every canvas page in
 * the WindPlatform shell + its mounted feature modules.
 *
 * H1 size matches the sidebar's WindPlatform wordmark (26 px) so the
 * chrome and canvas read at the same typographic tier. Christian
 * 2026-05-12 (visual review): *"the wind platform text height is equal
 * to the text of the h1 height."* We shrink the H1 rather than
 * enlarging the wordmark — bumping the sidebar's brand mark would
 * force a wider sidebar.
 *
 * History: before 2026-05-12 each page picked its own `variant` —
 * `h4`, `h5`, inline `display-sm`, even `h3` on the 403 page — so the
 * cross-page rhythm was uneven. MUI's defaults (h5 ≈ 24, h4 ≈ 34)
 * don't land on 26 px, so we size explicitly. Weight stays `semibold`
 * and tracking stays `title-lg` (-0.014em), matching the theme's
 * previous h4 overrides.
 *
 * Don't pass `variant` or `component` — they're fixed by design. To
 * preserve a surrounding flex row (e.g. Story 051's count badge next
 * to "Data Acquisitions"), put PageHeading inside the flex container;
 * PageHeading just replaces the `<h1>` element itself.
 */
export interface PageHeadingProps
  extends Omit<TypographyProps, "variant" | "component" | "children" | "ref"> {
  children: ReactNode;
  /**
   * Imperative ref onto the rendered `<h1>` element — used by pages
   * that need to programmatically focus the heading after navigation
   * (e.g. Settings' `tabIndex={-1}` skip-link target).
   */
  ref?: Ref<HTMLHeadingElement>;
}

export function PageHeading({
  children,
  ref,
  sx,
  ...rest
}: PageHeadingProps): ReturnType<typeof Typography> {
  // Merge the caller's `sx` on top of our canonical font block. MUI's
  // `sx` accepts an array; flatten the caller's sx into it whether it
  // arrived as a single value, an array, or undefined.
  const baseSx = {
    fontSize: 26,
    fontWeight: typography.weight.semibold,
    letterSpacing: typography.tracking["title-lg"],
    lineHeight: typography.lineHeight.tight,
  };
  const callerSx: SxProps<Theme> =
    sx === undefined ? [] : Array.isArray(sx) ? sx : [sx];
  const mergedSx = ([baseSx] as SxProps<Theme>[]).concat(
    callerSx as SxProps<Theme>,
  );
  return (
    <Typography
      ref={ref}
      component="h1"
      sx={mergedSx as SxProps<Theme>}
      {...rest}
    >
      {children}
    </Typography>
  );
}
